import React, {useEffect, useState} from 'react'
import {Box, Button, Flex, Stack, Text} from '../../vanilla'
import {observer} from 'mobx-react-lite'
import {MealDealProductTile} from './meal-deal-product-card'
import {useProductStore, useGlobalStore, useRapidDeliveryStore} from '../../store/hooks/useStore'
import {Hit} from '../../store/ProductStore'
import {transformHit} from '../../components/algolia/util/transformHit'
import {useHitsProductBadges} from '../../components/algolia/util/useHitsProductBadges'
import {numbersToWords} from '../../utils/utils'
import {MealDealBuilderGroupState} from './meal-deal-builder-helper'
import {CheckCircleIcon} from '../../components/icons'
import {vars} from '../../vanilla/vars.css'

interface MealDealItemProps {
  index: number
  steps: number
  label: string
  req: string
  verb?: string
  productsIds: string[]
  stepRef: React.Ref<HTMLDivElement>
  setCompleteness: (complete: boolean) => void
  groupState?: MealDealBuilderGroupState
  currentBasket: Array<{ productId: string | undefined, quantity: number | undefined }>
}

const ITEMS_IN_ROW = 6

const MealDealItemContainer: React.FC<MealDealItemProps> = ({
  index,
  label,
  req: reqProp,
  verb = 'Choose',
  productsIds,
  setCompleteness,
  groupState,
  stepRef,
  steps,
  currentBasket
}) => {
  const req = parseInt(reqProp, 10)
  const [itemsToShowCount, setItemsToShowCount] = useState(ITEMS_IN_ROW)
  const number = index + 1
  const selection = groupState?.totalSelected ?? 0
  const productStore = useProductStore()
  const {selectedPriceDayOffset, selectedStoreId, selectedRegion} = useGlobalStore()
  const {isRapidPricingActive} = useRapidDeliveryStore()
  // Grab all products to pass into ProductTile and order by isAddToCartDisabled
  const products = productsIds
    .map((id) => productStore.productsById[id] as unknown as Hit)
    .sort((a, b) => Number(a.isAddToCartDisabled) - Number(b.isAddToCartDisabled))
  // Fetch product badges
  useHitsProductBadges(products)

  useEffect(() => {
    setCompleteness(selection >= req)
  }, [selection, req])

  const [maxQuantityStates, setMaxQuantityStates] = useState<{[productId: string]: boolean}>({})

  const handleMaxQuantityState = (productId: string, isVisible: boolean) => {
    setMaxQuantityStates((prev) => {
      if (prev[productId] === isVisible) return prev
      return {...prev, [productId]: isVisible}
    })
  }

  const hasMaxQuantity = Object.values(maxQuantityStates).some((visible) => visible === true)

  return (
    <Box id={`step-${number}`} data-test-selector="meal-deal-section" ref={stepRef}>
      <Stack
        borderRadius="base"
        gap={['0px', '8px']}
        style={{outline: groupState?.isComplete ? `1px solid ${vars.color.accent1}` : undefined}}
      >
        <Flex
          as="div"
          width="full"
          bg="white"
          flexDirection="row"
          alignItems="flex-start"
          justifyContent="space-between"
          padding={['16px', '8px']}
          paddingBottom="0px"
          borderRadius={['none', 'base']}
          borderTopLeftRadius="base"
          borderTopRightRadius="base"
          gap={['0px', '8px']}
        >
          <Flex
            flexDirection="column"
            justifyContent="flex-start"
            paddingBottom={['16px', '8px']}
            gap={['2px', '0px']}
          >
            <Text color={groupState?.isComplete ? 'accent1' : 'gray400'}>
              Step {number} of {steps}
            </Text>
            <Text variant="heading1">
              {verb} {numbersToWords(req) || req} {label}
            </Text>
          </Flex>
          {groupState?.isComplete && <CheckCircleIcon color="green" boxSize="24px" />}
        </Flex>
        <Box 
          display ='grid'
          gridTemplateColumns={['1x', '2x', '3x']}
          gap={['0px', '8px']}
          justifyContent={'center'}
          data-test-selector="meal-deal-section-products"
        >
          {products.slice(0, itemsToShowCount).map((hit, productIndex) => {
            const isLastCard = productIndex === products.slice(0, itemsToShowCount).length - 1
            return (
              <MealDealProductTile
                hit={
                  transformHit(hit, selectedPriceDayOffset, selectedStoreId, selectedRegion, isRapidPricingActive) as Hit
                }
                groupState={groupState}
                isLastCard={isLastCard}
                currentBasket={currentBasket}
                onQuantityStateChange={(isVisible) => handleMaxQuantityState(hit.id, isVisible)}
                showQuantityReservedSpace={hasMaxQuantity}
                key={`${hit.id}-${productIndex}`}
              />
            )
          })}
        </Box>
        {productsIds?.length > itemsToShowCount ? (
            <Flex justifyContent="center" alignItems="center" marginTop="12px" marginBottom={groupState?.isComplete ? "18px" : "0px"}>
              <Button
                variant="outlineDark"
                onClick={() => {
                  if (itemsToShowCount < productsIds.length) {
                    setItemsToShowCount(itemsToShowCount + ITEMS_IN_ROW)
                  }
                }}
              >
                Show More {label}
              </Button>
            </Flex>
          ) : null}
      </Stack>
    </Box>
  )
}

export const MealDealItem = observer(MealDealItemContainer)
